import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { downloadCsv } from "./InventoryHistoryTab";
import { Upload, Download, FileSpreadsheet, CheckCircle2, XCircle, Plus, X } from "lucide-react";

export const POSProductIO = () => {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showSingle, setShowSingle] = useState(false);
  const fileRef = useRef(null);

  const onFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/pos/products/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      const ok = (r.data.records_created || 0) + (r.data.records_updated || 0);
      if (ok > 0) toast.success(`Imported ${ok} product(s)${r.data.records_failed ? ` · ${r.data.records_failed} rejected` : ""}`);
      else toast.warning(`No products imported · ${r.data.records_failed || 0} row(s) rejected`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const Sum = ({ label, v, c = "text-brand-navy" }) => (
    <div className="bg-slate-50 rounded-xl p-3"><div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</div><div className={`font-heading text-2xl ${c}`}>{v}</div></div>
  );

  return (
    <div className="space-y-4" data-testid="pos-product-io-tab">
      <div className="font-heading text-2xl text-brand-navy">Product Import / Export</div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <FileSpreadsheet className="h-6 w-6 text-brand-red" />
          <div className="font-heading text-lg text-brand-navy mt-2">Simple product file</div>
          <p className="text-sm text-slate-600 mt-1">Columns: <b>SKU, Purchase Receipt, Product Name, Type, Qty, Compare at Price, Cost, Our Price, Sale Price</b>. Required: SKU, Product Name, Our Price, Qty. <b>Type</b> must be <b>Merchandise</b> or <b>Clothing</b>. <b>Sale Price</b> is optional — set a lower “now” price to put the new item on sale. The SKU is also the barcode — there is no separate barcode column. Leave a cell blank for no value.</p>
          <div className="flex flex-wrap gap-2 mt-3">
            <button data-testid="pos-download-template" onClick={() => downloadCsv("/pos/products/template", {}, "pos-products-template.csv")}
              className="inline-flex items-center gap-1 bg-brand-navy text-white rounded-full h-10 px-4 font-bold text-sm"><Download className="h-4 w-4" /> Template</button>
            <button data-testid="pos-export-products" onClick={() => downloadCsv("/pos/products/export", {}, "pos-products.csv")}
              className="inline-flex items-center gap-1 border border-brand-navy text-brand-navy rounded-full h-10 px-4 font-bold text-sm"><Download className="h-4 w-4" /> Export catalog</button>
            <button data-testid="pos-add-single-btn" onClick={() => setShowSingle(true)}
              className="inline-flex items-center gap-1 bg-brand-green text-white rounded-full h-10 px-4 font-bold text-sm"><Plus className="h-4 w-4" /> Add single item</button>
          </div>
        </div>

        <label className="border-2 border-dashed border-slate-300 rounded-2xl p-8 text-center cursor-pointer hover:border-brand-navy block" data-testid="pos-import-dropzone">
          <Upload className="h-8 w-8 mx-auto text-slate-400" />
          <div className="font-bold mt-2">{busy ? "Importing…" : "Upload product CSV"}</div>
          <div className="text-xs text-slate-500">Max 5MB · header row required</div>
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" data-testid="pos-import-file-input" onChange={(e) => onFile(e.target.files?.[0])} />
        </label>
      </div>

      {result && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="pos-import-result">
          <div className="flex items-center gap-2">
            {result.records_failed > 0 ? <XCircle className="h-6 w-6 text-brand-red" /> : <CheckCircle2 className="h-6 w-6 text-emerald-600" />}
            <div className="font-heading text-lg text-brand-navy">Import {String(result.status || "").toLowerCase()}</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-3">
            <Sum label="Processed" v={result.records_processed} />
            <Sum label="Created" v={result.records_created} c="text-emerald-600" />
            <Sum label="Updated" v={result.records_updated} c="text-brand-navy" />
            <Sum label="Skipped" v={result.records_skipped} c="text-slate-500" />
            <Sum label="Rejected" v={result.records_failed} c="text-brand-red" />
          </div>
          {result.rejected?.length > 0 && (
            <div className="mt-4">
              <div className="font-bold text-brand-red mb-1">Rejected rows</div>
              <div className="overflow-x-auto border border-slate-200 rounded-xl max-h-72">
                <table className="w-full text-xs" data-testid="pos-rejected-table">
                  <thead className="bg-slate-50 text-left uppercase text-slate-500 sticky top-0"><tr><th className="p-2">Row</th><th className="p-2">SKU</th><th className="p-2">Reason</th></tr></thead>
                  <tbody>{result.rejected.map((e, i) => <tr key={i} className="border-t border-slate-100"><td className="p-2">{e.row}</td><td className="p-2 font-bold">{e.sku || "—"}</td><td className="p-2 text-brand-red">{e.reason}</td></tr>)}</tbody>
                </table>
              </div>
            </div>
          )}
          {result.warned?.length > 0 && (
            <div className="mt-4">
              <div className="font-bold text-amber-600 mb-1">Rows with warnings</div>
              <div className="overflow-x-auto border border-slate-200 rounded-xl max-h-72">
                <table className="w-full text-xs" data-testid="pos-warned-table">
                  <thead className="bg-slate-50 text-left uppercase text-slate-500 sticky top-0"><tr><th className="p-2">Row</th><th className="p-2">SKU</th><th className="p-2">Note</th></tr></thead>
                  <tbody>{result.warned.map((e, i) => <tr key={i} className="border-t border-slate-100"><td className="p-2">{e.row}</td><td className="p-2 font-bold">{e.sku || "—"}</td><td className="p-2 text-amber-600">{e.reason}</td></tr>)}</tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
      {showSingle && <SingleItemModal onClose={() => setShowSingle(false)} />}
    </div>
  );
};

const SIField = ({ label, k, value, onChange, type = "text", placeholder, required }) => (
  <label className="block">
    <span className="text-xs font-bold text-slate-600">{label}{required && " *"}</span>
    <input type={type} value={value} onChange={onChange} placeholder={placeholder} data-testid={`single-${k}`}
      className="mt-1 w-full h-10 border border-slate-200 rounded-lg px-3" />
  </label>
);

const SingleItemModal = ({ onClose }) => {
  const [f, setF] = useState({ sku: "", purchase_receipt: "", product_name: "", product_type: "Merchandise", qty: "1", compare_at_price: "", cost: "", price: "", sale_price: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    if (!f.sku.trim() || !f.product_name.trim() || f.price === "" || f.qty === "") { toast.error("SKU, Name, Our Price and Qty are required"); return; }
    setSaving(true);
    try {
      const body = {
        sku: f.sku.trim(), product_name: f.product_name.trim(), product_type: f.product_type,
        purchase_receipt: f.purchase_receipt.trim(), qty: parseInt(f.qty, 10) || 0, price: parseFloat(f.price) || 0,
        compare_at_price: f.compare_at_price === "" ? null : parseFloat(f.compare_at_price),
        cost: f.cost === "" ? null : parseFloat(f.cost),
        sale_price: f.sale_price === "" ? null : parseFloat(f.sale_price),
      };
      const r = await api.post("/pos/products/single", body);
      toast.success(`Added ${r.data.product_name} (${r.data.sku})`);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create product"); }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="single-item-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 bg-white">
          <h2 className="font-heading text-xl text-brand-navy">Add single product</h2>
          <button onClick={onClose} data-testid="single-close"><X className="h-5 w-5 text-slate-500" /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <SIField label="SKU / Barcode" k="sku" value={f.sku} onChange={set("sku")} placeholder="1961533" required />
          <SIField label="Purchase Receipt" k="purchase_receipt" value={f.purchase_receipt} onChange={set("purchase_receipt")} placeholder="14444" />
          <div className="col-span-2"><SIField label="Product Name" k="product_name" value={f.product_name} onChange={set("product_name")} placeholder="Benetton SS Top S" required /></div>
          <label className="block">
            <span className="text-xs font-bold text-slate-600">Type</span>
            <select value={f.product_type} onChange={set("product_type")} data-testid="single-product_type" className="mt-1 w-full h-10 border border-slate-200 rounded-lg px-2">
              <option>Merchandise</option><option>Clothing</option>
            </select>
          </label>
          <SIField label="Quantity" k="qty" value={f.qty} onChange={set("qty")} type="number" placeholder="1" required />
          <SIField label="Compare at Price" k="compare_at_price" value={f.compare_at_price} onChange={set("compare_at_price")} type="number" placeholder="13.99" />
          <SIField label="Cost" k="cost" value={f.cost} onChange={set("cost")} type="number" placeholder="2.80" />
          <div className="col-span-2"><SIField label="Our Price" k="price" value={f.price} onChange={set("price")} type="number" placeholder="9.79" required /></div>
          <div className="col-span-2"><SIField label="Sale Price (optional — the 'now' price)" k="sale_price" value={f.sale_price} onChange={set("sale_price")} type="number" placeholder="6.99" /></div>
        </div>
        <div className="px-5 py-4 border-t sticky bottom-0 bg-white flex justify-end gap-2">
          <button onClick={onClose} className="h-11 px-4 rounded-lg border border-slate-200 text-slate-600 font-bold">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="single-save" className="h-11 px-5 rounded-lg bg-brand-green text-white font-bold disabled:opacity-60">{saving ? "Saving…" : "Create product"}</button>
        </div>
      </div>
    </div>
  );
};
